import {
  AppWindow,
  ClipboardList,
  LayoutGrid,
  Settings,
  type LucideIcon,
} from "lucide-react";
import type {
  ModuleManifest,
  SearchScope,
  SearchSection,
  Tab,
} from "@playbookai/ui";
import type {
  PlatformModuleId,
  PlatformProviderFailure,
  PlatformSearchResult,
} from "@playbookai/types";
import {
  manifestsToLauncherEntries,
  moduleAccentTextClass,
  moduleIcon,
} from "../modules/launcher-entries.js";
import type { SettingsSearchItem } from "../settings/sections.js";
import type { PlatformSearchStatus } from "./usePlatformSearch.js";

/**
 * Host-side providers for the `UniversalSearchDialog` — the dialog stays
 * data-blind; this module projects real shell state (open tabs, enabled
 * modules) and permission-filtered platform results into `SearchSection`s.
 * Item ids are namespaced so the select handler can recover the typed result;
 * routes never travel through search data.
 */
export const SEARCH_SCOPES: SearchScope[] = [
  { id: "tabs", label: "Open tabs", icon: AppWindow },
  { id: "apps", label: "Apps", icon: LayoutGrid },
  { id: "work", label: "Work", icon: ClipboardList },
  { id: "settings", label: "Settings", icon: Settings },
];

/** Where the platform search stands, for the Work section to show. */
export interface WorkSearchState {
  readonly status: PlatformSearchStatus;
  readonly partialFailures: readonly PlatformProviderFailure[];
  readonly retry: () => void;
}

export function buildSearchSections(
  tabs: readonly Tab[],
  modules: readonly ModuleManifest[],
  results: readonly PlatformSearchResult[] = [],
  settings: readonly SettingsSearchItem[] = [],
  work?: WorkSearchState,
): SearchSection[] {
  return [
    {
      id: "tabs",
      label: "Open tabs",
      scopeIds: ["tabs"],
      items: tabs.map((t) => ({
        id: `tab:${t.id}`,
        title: t.label,
        meta: t.module ? `${t.module} · ${t.route}` : t.route,
        icon: t.icon ?? AppWindow,
      })),
    },
    {
      id: "apps",
      label: "Apps",
      scopeIds: ["apps"],
      items: manifestsToLauncherEntries(modules).map((e) => ({
        id: `app:${e.id}`,
        title: e.name,
        meta: e.blurb,
        icon: e.icon,
        accent: e.accent,
      })),
    },
    {
      id: "work",
      label: "Work",
      scopeIds: ["work"],
      items: results.map((result) => ({
        id: platformSearchItemId(result),
        title: result.title,
        meta: platformSearchMeta(result),
        icon: MODULE_ICONS[result.ref.module],
        accent: MODULE_ACCENTS[result.ref.module],
      })),
      ...(work ? workSectionState(work) : {}),
    },
    {
      id: "settings",
      label: "Settings",
      scopeIds: ["settings"],
      // Ids only (`settings:<sectionId>`); the select handler resolves the
      // route through the registry, so no route travels through search data.
      items: settings.map((item) => ({
        id: item.id,
        title: item.title,
        meta: item.meta,
        icon: item.icon,
      })),
    },
  ];
}

const MODULE_LABELS = {
  solve: "SOLVE",
  vsm: "VSM",
  hoshin: "Hoshin",
  lama: "LAMA",
  kpi: "KPI",
} as const;

/* The module identity ramp, read from the ONE presentation table (ADR-0092);
   KPI has no module manifest yet, so it keeps the gold it shares with Hoshin. */
/* The module's MARK, for the same rows the accents above paint. A Work row
   already carried its module's identity COLOUR while drawing a clipboard, so
   colour and glyph disagreed on every result: a value stream came back teal,
   shaped like a clipboard.

   This cannot just call `moduleIcon()`. That takes the shell's `ModuleId`,
   while a platform search result carries `PlatformModuleId`, which includes
   `kpi` — a module with no manifest yet. So the table is spelled out here and
   kpi keeps the neutral work glyph, exactly as it keeps Hoshin's gold above. */
const MODULE_ICONS: Record<PlatformModuleId, LucideIcon> = {
  solve: moduleIcon("solve"),
  vsm: moduleIcon("vsm"),
  hoshin: moduleIcon("hoshin"),
  lama: moduleIcon("lama"),
  kpi: ClipboardList,
};

const MODULE_ACCENTS = {
  solve: moduleAccentTextClass("solve"),
  vsm: moduleAccentTextClass("vsm"),
  hoshin: moduleAccentTextClass("hoshin"),
  lama: moduleAccentTextClass("lama"),
  kpi: "text-pb-gold-text",
} as const;

/* A slow or failed search must not read as "No matches" (the dialog draws
   its status in place of the items), and a search where some modules did
   not answer says which. */
function workSectionState(
  work: WorkSearchState,
): Pick<
  SearchSection,
  "status" | "loadingLabel" | "errorTitle" | "onRetry" | "notice"
> {
  if (work.status === "searching")
    return { status: "loading", loadingLabel: "Searching your work" };
  if (work.status === "failed")
    return {
      status: "error",
      errorTitle: "Couldn’t search your work right now.",
      onRetry: work.retry,
    };
  if (work.status === "done" && work.partialFailures.length > 0)
    return { notice: partialFailureNotice(work.partialFailures) };
  return {};
}

export function partialFailureNotice(
  failures: readonly PlatformProviderFailure[],
): string {
  const names = [
    ...new Set(failures.map((failure) => providerLabel(failure.provider))),
  ];
  if (names.some((name) => name === null))
    return "Some apps didn’t answer, so some work may be missing.";
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${list} didn’t answer, so some work may be missing.`;
}

function providerLabel(
  provider: PlatformProviderFailure["provider"],
): string | null {
  return Object.hasOwn(MODULE_LABELS, provider)
    ? MODULE_LABELS[provider as PlatformModuleId]
    : null;
}

export function platformSearchItemId(result: PlatformSearchResult): string {
  return `entity:${result.ref.module}:${result.ref.entity_type}:${result.ref.entity_id}`;
}

function platformSearchMeta(result: PlatformSearchResult): string {
  return [
    MODULE_LABELS[result.ref.module],
    result.container_label,
    ...result.badges.map((badge) => badge.label),
    result.description,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" · ");
}
